import {
  SUN,
  TAU,
  eciToEcef,
  gmst,
  moonPosition,
  planetPosition,
  planetSemiMajorAxis,
  sunPosition,
  type PlanetName,
} from '@trajectory/astro';

export interface MapView {
  zoom: number;
  offsetX: number;
  offsetY: number;
}

export interface GeographicPoint {
  longitudeDeg: number;
  latitudeDeg: number;
}

export const MIN_MAP_ZOOM = 1;
export const MAX_MAP_ZOOM = 12;

export function clampMapZoom(zoom: number): number {
  return Math.min(MAX_MAP_ZOOM, Math.max(MIN_MAP_ZOOM, zoom));
}

/**
 * Zoom around the pointer instead of the canvas centre. Offsets are screen
 * pixels, which keeps panning predictable at every zoom level.
 */
export function zoomMapView(
  view: MapView,
  requestedZoom: number,
  anchorX: number,
  anchorY: number,
  width: number,
  height: number,
): MapView {
  const zoom = clampMapZoom(requestedZoom);
  if (zoom === view.zoom) return view;
  const centreX = width / 2;
  const centreY = height / 2;
  const worldX = (anchorX - centreX - view.offsetX) / view.zoom;
  const worldY = (anchorY - centreY - view.offsetY) / view.zoom;
  return {
    zoom,
    offsetX: anchorX - centreX - worldX * zoom,
    offsetY: anchorY - centreY - worldY * zoom,
  };
}

/** Geographic location directly below the Sun in the Earth-fixed frame. */
export function subsolarPoint(epochSeconds: number): GeographicPoint {
  const fixed = eciToEcef(sunPosition(epochSeconds), gmst(epochSeconds));
  return {
    longitudeDeg: (Math.atan2(fixed[1], fixed[0]) * 180) / Math.PI,
    latitudeDeg: (Math.atan2(fixed[2], Math.hypot(fixed[0], fixed[1])) * 180) / Math.PI,
  };
}

export function niceScaleDistance(metersPerPixel: number, targetPixels = 96): number {
  if (!Number.isFinite(metersPerPixel) || metersPerPixel <= 0) return 0;
  const target = metersPerPixel * targetPixels;
  const magnitude = 10 ** Math.floor(Math.log10(target));
  const normalized = target / magnitude;
  const factor = normalized >= 5 ? 5 : normalized >= 2 ? 2 : 1;
  return factor * magnitude;
}

export function formatMapDistance(meters: number): string {
  if (meters >= 1e9) return `${(meters / 1e9).toFixed(meters < 10e9 ? 1 : 0)} Gm`;
  if (meters >= 1e6) return `${(meters / 1e6).toFixed(meters < 10e6 ? 1 : 0)} Mm`;
  if (meters >= 1000) return `${(meters / 1000).toFixed(meters < 10_000 ? 1 : 0)} km`;
  return `${Math.round(meters)} m`;
}

export function nearestMapPointIndex(
  points: readonly (readonly [number, number])[],
  pointerX: number,
  pointerY: number,
  maximumDistancePixels = 14,
): number {
  let nearestIndex = -1;
  let nearestDistanceSquared = maximumDistancePixels * maximumDistancePixels;
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index]!;
    const distanceSquared = (point[0] - pointerX) ** 2 + (point[1] - pointerY) ** 2;
    if (distanceSquared <= nearestDistanceSquared) {
      nearestDistanceSquared = distanceSquared;
      nearestIndex = index;
    }
  }
  return nearestIndex;
}

export function planetOrbitPoints(
  planet: PlanetName,
  epochSeconds: number,
  sampleCount = 180,
): readonly (readonly [number, number])[] {
  const radius = planetSemiMajorAxis(planet);
  const period = TAU * Math.sqrt((radius * radius * radius) / SUN.mu);
  const points: [number, number][] = [];
  for (let index = 0; index <= sampleCount; index += 1) {
    const position = planetPosition(planet, epochSeconds + (period * index) / sampleCount);
    points.push([position[0], position[1]]);
  }
  return points;
}

export function moonOrbitPoints(
  epochSeconds: number,
  sampleCount = 144,
): readonly (readonly [number, number])[] {
  // Kepler's third law around Earth, using the same mean radius as the display
  // scale. moonPosition adds the principal lunar perturbation terms.
  const siderealPeriod = 27.321661 * 86400;
  const points: [number, number][] = [];
  for (let index = 0; index <= sampleCount; index += 1) {
    const position = moonPosition(epochSeconds + (siderealPeriod * index) / sampleCount);
    points.push([position[0], position[1]]);
  }
  return points;
}

export function mapFrameDescription(referenceFrame: string, centralBodyId: string): string {
  if (referenceFrame === 'heliocentric') return 'Heliocentric equatorial projection';
  if (referenceFrame === 'earth-centered') return 'Earth–Moon inertial projection';
  if (referenceFrame === 'earth-inertial') return 'Earth-fixed ground track';
  return `${centralBodyId[0]?.toUpperCase() ?? ''}${centralBodyId.slice(1)}-centred inertial projection`;
}
